import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  Box, 
  Button, 
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Typography,
  Divider
} from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import { 
  Delete as DeleteIcon, 
  Link as LinkIcon, 
  Undo as UndoIcon, 
  Redo as RedoIcon, 
  ZoomIn as ZoomInIcon, 
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  ColorLens as ColorLensIcon,
  Print as PrintIcon,
  Person as CharacterIcon,
  Place as LocationIcon,
  Category as PropIcon,
  Lightbulb as ThemeIcon,
  Bolt as ConflictIcon,
  Event as EventIcon,
  Note as NoteIcon,
  QuestionMark as QuestionIcon,
  EmojiObjects as IdeaIcon
} from '@mui/icons-material';
import { getMindMap, saveMindMap } from '../../services/storyToolsService';
import ProModal from '../common/ProModal';
// MarkerType is imported below with other ReactFlow components
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  useNodesState, 
  useEdgesState, 
  addEdge,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';

// Custom node component for Scapple-like mind mapping with professional styling
const NODE_COLOR_OPTIONS = [
  // Neutrals
  { value: '#f8fafc', label: 'Snow' },
  { value: '#334155', label: 'Slate' },
  // Blues
  { value: '#dbeafe', label: 'Sky Mist' },
  { value: '#3b82f6', label: 'Ocean Blue' },
  { value: '#1e40af', label: 'Deep Navy' },
  // Greens
  { value: '#dcfce7', label: 'Mint Cream' },
  { value: '#22c55e', label: 'Emerald' },
  { value: '#15803d', label: 'Forest' },
  // Purples
  { value: '#f3e8ff', label: 'Lavender' },
  { value: '#a855f7', label: 'Violet' },
  { value: '#7c3aed', label: 'Royal Purple' },
  // Warm tones
  { value: '#fef3c7', label: 'Buttercream' },
  { value: '#fbbf24', label: 'Golden' },
  { value: '#f97316', label: 'Tangerine' },
  // Reds & Pinks
  { value: '#fce7f3', label: 'Blush' },
  { value: '#ec4899', label: 'Hot Pink' },
  { value: '#ef4444', label: 'Coral Red' },
  // Teals
  { value: '#ccfbf1', label: 'Seafoam' },
  { value: '#14b8a6', label: 'Teal' }
];

const FONT_SIZE_OPTIONS = [
  { value: '12px', label: 'Small' },
  { value: '14px', label: 'Regular' },
  { value: '16px', label: 'Large' },
  { value: '18px', label: 'Headline' }
];

const SHAPE_OPTIONS = [
  { value: 'rectangle', label: 'Rectangle' },
  { value: 'capsule', label: 'Capsule' },
  { value: 'diamond', label: 'Diamond' },
  { value: 'text', label: 'Text Only' }
];

// Node categories with auto-colors and icons for film-centric workflow
const NODE_CATEGORY_OPTIONS = [
  { value: 'note', label: 'General Note', color: '#f8fafc', icon: 'note' },
  { value: 'character', label: 'Character', color: '#3b82f6', icon: 'character' },
  { value: 'location', label: 'Location', color: '#22c55e', icon: 'location' },
  { value: 'prop', label: 'Prop', color: '#f97316', icon: 'prop' },
  { value: 'theme', label: 'Theme', color: '#a855f7', icon: 'theme' },
  { value: 'conflict', label: 'Conflict', color: '#ef4444', icon: 'conflict' },
  { value: 'event', label: 'Event', color: '#fbbf24', icon: 'event' },
  { value: 'question', label: 'Question', color: '#14b8a6', icon: 'question' },
  { value: 'idea', label: 'Idea', color: '#ec4899', icon: 'idea' }
];

// Map category to icon component
const getCategoryIcon = (category, size = 16) => {
  const iconStyle = { fontSize: size, opacity: 0.85 };
  switch (category) {
    case 'character': return <CharacterIcon sx={iconStyle} />;
    case 'location': return <LocationIcon sx={iconStyle} />;
    case 'prop': return <PropIcon sx={iconStyle} />;
    case 'theme': return <ThemeIcon sx={iconStyle} />;
    case 'conflict': return <ConflictIcon sx={iconStyle} />;
    case 'event': return <EventIcon sx={iconStyle} />;
    case 'question': return <QuestionIcon sx={iconStyle} />;
    case 'idea': return <IdeaIcon sx={iconStyle} />;
    case 'note':
    default: return <NoteIcon sx={iconStyle} />;
  }
};


const CustomNodeComponent = ({ data, selected, id }) => {
  // Access dark mode from data props
  const { darkMode } = data;
  
  // Determine shape and style based on node properties
  const getNodeShape = useCallback((shape) => {
    switch(shape) {
      case 'circle':
        return { 
          borderRadius: '50%', 
          aspectRatio: '1/1', 
          minWidth: '100px',
          boxShadow: darkMode ? 
            '0 2px 8px rgba(0,0,0,0.4)' : 
            '0 2px 5px rgba(0,0,0,0.15)' 
        };
      case 'capsule':
        return { 
          borderRadius: '50px', 
          minWidth: '120px',
          minHeight: '40px',
          boxShadow: darkMode ? 
            '0 2px 8px rgba(0,0,0,0.4)' : 
            '0 2px 5px rgba(0,0,0,0.15)'
        };
      case 'diamond':
        return { 
          transform: 'rotate(45deg)', 
          aspectRatio: '1/1', 
          minWidth: '100px',
          boxShadow: darkMode ? 
            '0 2px 8px rgba(0,0,0,0.4)' : 
            '0 2px 5px rgba(0,0,0,0.15)'
        };
      case 'text':
        return { 
          borderRadius: '0', 
          minWidth: '50px',
          border: 'none',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          padding: '4px 8px'
        };
      case 'note':
        return { 
          borderRadius: '2px', 
          minWidth: '150px',
          border: darkMode ? 
            '1px solid rgba(255,255,255,0.15)' : 
            '1px solid rgba(0,0,0,0.15)',
          backgroundColor: darkMode ? '#2d2d2d' : '#f9f9f9',
          boxShadow: darkMode ? 
            '0 2px 8px rgba(0,0,0,0.4)' : 
            '0 2px 4px rgba(0,0,0,0.1)'
        };
      case 'rectangle':
      default:
        return { 
          borderRadius: '4px', 
          minWidth: '120px',
          boxShadow: darkMode ? 
            '0 2px 8px rgba(0,0,0,0.4)' : 
            '0 2px 5px rgba(0,0,0,0.15)'
        };
    }
  }, [darkMode]);
  
  // Calculate text color based on background brightness for better contrast
  const getTextColor = useCallback((bgColor) => {
    // Convert hex to RGB
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate brightness
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // Return white for dark backgrounds, black for light
    return brightness > 125 ? '#000000' : '#ffffff';
  }, []);
  
  // Memoize shape style and text color calculations
  const shapeStyle = useMemo(() => getNodeShape(data.shape || 'rectangle'), [data.shape, getNodeShape]);
  const textColor = useMemo(() => getTextColor(data.color || '#ffffff'), [data.color, getTextColor]);
  
  // Apply text styling from node data with professional typography
  const textStyle = useMemo(() => {
    return {
      fontWeight: data.bold ? 'bold' : 'normal',
      fontStyle: data.italic ? 'italic' : 'normal',
      textDecoration: data.underline ? 'underline' : 'none',
      fontSize: data.fontSize || '14px',
      color: textColor,
      wordBreak: 'break-word',
      textAlign: 'left',
      lineHeight: '1.4',
      letterSpacing: data.bold ? '0' : '0.01em',
      fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    };
  }, [data.bold, data.italic, data.underline, data.fontSize, textColor]);
  
  // Base node style - Scapple-like appearance with dark mode support
  // Memoized to prevent recalculation on every render
  const isTextOnly = data.shape === 'text';
  const nodeStyle = useMemo(() => ({
    padding: isTextOnly ? '4px 8px' : '12px 14px',
    border: isTextOnly 
      ? (selected ? `1px dashed ${darkMode ? '#5d9cff' : '#2979ff'}` : 'none')
      : (selected 
        ? `2px solid ${darkMode ? '#5d9cff' : '#2979ff'}` 
        : `1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)'}`),
    borderRadius: data.shape === 'note' ? '2px' : '4px',
    backgroundColor: isTextOnly ? 'transparent' : (data.color || (darkMode ? '#333333' : '#ffffff')),
    color: isTextOnly ? (data.color || (darkMode ? '#e0e0e0' : '#333333')) : (data.color ? textColor : (darkMode ? '#e0e0e0' : '#000000')),
    minHeight: isTextOnly ? 'auto' : '36px',
    minWidth: isTextOnly ? 'auto' : '80px',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    position: 'relative',
    boxShadow: isTextOnly 
      ? 'none' 
      : (selected 
        ? `0 0 8px ${darkMode ? 'rgba(93,156,255,0.6)' : 'rgba(41,121,255,0.5)'}` 
        : `0 1px 3px ${darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.08)'}`),
    transition: 'box-shadow 0.15s ease, border 0.15s ease',
    cursor: 'grab',
    fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    ...shapeStyle
  }), [selected, darkMode, data.shape, data.color, textColor, shapeStyle, isTextOnly]);
  
  // Content style needs to counter-rotate if parent is rotated (diamond shape)
  // Memoized to prevent recalculation
  const contentStyle = useMemo(() => ({
    transform: data.shape === 'diamond' ? 'rotate(-45deg)' : 'none',
    textAlign: 'left',
    wordBreak: 'break-word',
    maxWidth: '200px', // Wrap text after ~50 characters (roughly 4px per char)
    padding: '4px',
    lineHeight: '1.5',
    letterSpacing: '0.01em',
    pointerEvents: 'none',
    whiteSpace: 'pre-wrap',
    ...textStyle
  }), [data.shape, textStyle]);
  
  // Center handle style - defined once outside to avoid recreation
  const handleStyle = useMemo(() => ({ 
    width: 24, 
    height: 24, 
    background: 'transparent',
    border: 'none',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    opacity: 0,
    zIndex: 1,
    pointerEvents: 'all'
  }), []);
  
  // Get category icon
  const categoryIcon = useMemo(() => {
    if (data.category && data.category !== 'note') {
      return getCategoryIcon(data.category, 14);
    }
    return null;
  }, [data.category]);

  return (
    <div style={nodeStyle}>
      {/* Center handles for connections */}
      <Handle
        type="source"
        position={Position.Right}
        style={{ ...handleStyle, cursor: 'crosshair' }}
        isConnectable={true}
        id="center-source"
      />
      <Handle
        type="target"
        position={Position.Left}
        style={handleStyle}
        isConnectable={true}
        id="center-target"
      />
      {/* Image thumbnail if present */}
      {data.image && (
        <div style={{
          width: '100%',
          maxHeight: 60,
          marginBottom: 6,
          borderRadius: 4,
          overflow: 'hidden',
          pointerEvents: 'none'
        }}>
          <img 
            src={data.image} 
            alt="" 
            style={{ 
              width: '100%', 
              height: 'auto', 
              objectFit: 'cover',
              display: 'block'
            }} 
          />
        </div>
      )}
      {/* Content with optional category icon */}
      <div style={{ ...contentStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
        {categoryIcon}
        <span>{data.label}</span>
      </div>
    </div>
  );
};

// Apply memoization to prevent unnecessary re-renders
const CustomNode = React.memo(CustomNodeComponent);

// Traditional Mind Map component (Scapple-like)
const MindMapFlow = ({ projectId = 'default' }) => {
  // Get dark mode from theme context
  const { darkMode } = useTheme();
  const muiTheme = useMuiTheme();
  
  // State for nodes and edges
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  // Debounce save timer
  const saveTimerRef = useRef(null);
  const HISTORY_MAX = 50;

  // Create node types object with custom node - stable reference
  const nodeTypes = useMemo(() => ({
    custom: CustomNode
  }), []);

  // Add dark mode to node data
  const processNodes = useCallback((nodes) => {
    return nodes.map(node => {
      if (node.type === 'custom') {
        return {
          ...node,
          data: {
            ...node.data,
            darkMode
          }
        };
      }
      return node;
    });
  }, [darkMode]);
  
  // State for viewport control
  const [viewportTransform, setViewportTransform] = useState({ x: 0, y: 0, zoom: 1 });
  
  // State for dialog and node creation - expanded for Scapple-like styling
  const [openDialog, setOpenDialog] = useState(false);
  const [newNode, setNewNode] = useState({ 
    label: '', 
    group: 'note', 
    color: '#f0f0f0', 
    shape: 'rectangle',
    fontSize: '14px',
    bold: false,
    italic: false,
    underline: false,
    image: '',
    stackOrder: 0 // For stacking notes
  });
  
  // State for selected elements and editing
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState(null);
  const editingNodeRef = useRef(null); // Track node being edited (avoids async state issues)
  
  // State for toolbar actions
  const [isCreatingNode, setIsCreatingNode] = useState(false);
  const [isConnectingNodes, setIsConnectingNodes] = useState(false);
  const [connectingNodeId, setConnectingNodeId] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const reactFlowWrapper = useRef(null);
  const [showMiniMap, setShowMiniMap] = useState(true);
  
  // Define toolbar button style for consistent UI
  const toolbarButtonStyle = {
    color: darkMode ? '#e0e0e0' : '#555',
    padding: '4px',
    '&:hover': {
      backgroundColor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
    },
    '&.Mui-disabled': {
      color: darkMode ? '#555' : '#ccc'
    }
  };
  
  // Handle keyboard events for edge deletion
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedEdge) {
        deleteSelectedEdge();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEdge]);  // eslint-disable-line react-hooks/exhaustive-deps
  
  // Initialize ReactFlow
  const onInit = useCallback((instance) => {
    setReactFlowInstance(instance);
    setZoomLevel(instance.getZoom());
    
    // Set zoom to exactly 100% after initialization
    setTimeout(() => {
      instance.setViewport({ x: 0, y: 0, zoom: 1 });
      setZoomLevel(1);
      setViewportTransform({ x: 0, y: 0, zoom: 1 });
    }, 50);
  }, []);
  
  // Custom zoom handlers for Scapple-like zoom control
  const handleZoomIn = useCallback(() => {
    if (reactFlowInstance) {
      const currentZoom = reactFlowInstance.getZoom();
      const newZoom = Math.min(currentZoom * 1.2, 2.5);
      reactFlowInstance.zoomTo(newZoom);
      setZoomLevel(newZoom);
      setViewportTransform(reactFlowInstance.getViewport());
    }
  }, [reactFlowInstance]);
  
  const handleZoomOut = useCallback(() => {
    if (reactFlowInstance) {
      const currentZoom = reactFlowInstance.getZoom();
      const newZoom = Math.max(currentZoom * 0.8, 0.1);
      reactFlowInstance.zoomTo(newZoom);
      setZoomLevel(newZoom);
      setViewportTransform(reactFlowInstance.getViewport());
    }
  }, [reactFlowInstance]);
  
  const handleFitView = useCallback(() => {
    if (reactFlowInstance) {
      reactFlowInstance.fitView({ padding: 0.2 });
      setZoomLevel(reactFlowInstance.getZoom());
      setViewportTransform(reactFlowInstance.getViewport());
    }
  }, [reactFlowInstance]);
  
  // Track zoom changes
  const onZoomChange = useCallback((e) => {
    if (reactFlowInstance) {
      setZoomLevel(reactFlowInstance.getZoom());
    }
  }, [reactFlowInstance]);

  // Print mind map function - uses iframe to print without opening new page
  const handlePrint = useCallback(() => {
    if (!nodes.length) {
      alert('No nodes to print');
      return;
    }
    
    // Estimate node dimensions based on label length
    const getNodeDimensions = (node) => {
      const label = node.data.label || '';
      const fontSize = parseInt(node.data.fontSize) || 14;
      const charWidth = fontSize * 0.6;
      const textWidth = label.length * charWidth;
      const width = Math.max(80, textWidth + 32);
      const height = 44;
      return { width, height };
    };

    // Calculate bounds of all nodes using actual dimensions
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    nodes.forEach(node => {
      const dim = getNodeDimensions(node);
      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      maxX = Math.max(maxX, node.position.x + dim.width);
      maxY = Math.max(maxY, node.position.y + dim.height);
    });
    
    const padding = 50;
    const width = maxX - minX + padding * 2;
    const height = maxY - minY + padding * 2;
    
    // Generate node HTML
    const nodesHtml = nodes.map(node => {
      const x = node.position.x - minX + padding;
      const y = node.position.y - minY + padding;
      const bgColor = node.data.color || '#f8fafc';
      const textColor = getContrastColor(bgColor);
      const fontSize = node.data.fontSize || '14px';
      const fontWeight = node.data.bold ? 'bold' : 'normal';
      const fontStyle = node.data.italic ? 'italic' : 'normal';
      const textDecoration = node.data.underline ? 'underline' : 'none';
      
      let borderRadius = '6px';
      if (node.data.shape === 'capsule') borderRadius = '50px';
      if (node.data.shape === 'diamond') borderRadius = '4px';
      
      const transform = node.data.shape === 'diamond' ? 'rotate(45deg)' : 'none';
      const innerTransform = node.data.shape === 'diamond' ? 'rotate(-45deg)' : 'none';
      
      return `
        <div style="
          position: absolute;
          left: ${x}px;
          top: ${y}px;
          background: ${bgColor};
          color: ${textColor};
          padding: 12px 16px;
          border-radius: ${borderRadius};
          font-size: ${fontSize};
          font-weight: ${fontWeight};
          font-style: ${fontStyle};
          text-decoration: ${textDecoration};
          box-shadow: 0 2px 6px rgba(0,0,0,0.12);
          border: 1px solid rgba(0,0,0,0.08);
          transform: ${transform};
          min-width: 80px;
          text-align: center;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        ">
          <span style="transform: ${innerTransform}; display: block;">${node.data.label || ''}</span>
        </div>
      `;
    }).join('');
    
    // Generate edge SVG lines - connect to actual node centers
    const edgesSvg = edges.map(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      if (!sourceNode || !targetNode) return '';
      
      const sourceDim = getNodeDimensions(sourceNode);
      const targetDim = getNodeDimensions(targetNode);
      
      // Calculate center of each node
      const x1 = sourceNode.position.x - minX + padding + sourceDim.width / 2;
      const y1 = sourceNode.position.y - minY + padding + sourceDim.height / 2;
      const x2 = targetNode.position.x - minX + padding + targetDim.width / 2;
      const y2 = targetNode.position.y - minY + padding + targetDim.height / 2;
      
      return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#666" stroke-width="2" stroke-linecap="round" opacity="0.7"/>`;
    }).join('');
    
    const bgColor = darkMode ? '#1a1a2e' : '#ffffff';
    
    // Determine page orientation - use landscape if content is wider than portrait page
    // Standard printable area (with 0.4in margins on letter paper)
    const portraitPageWidth = 720;  // ~7.5in
    const portraitPageHeight = 960; // ~10in
    const landscapePageWidth = 960;
    const landscapePageHeight = 720;
    
    const useLandscape = width > portraitPageWidth || width > height;
    
    // Calculate scale to fit content to page
    const pageWidth = useLandscape ? landscapePageWidth : portraitPageWidth;
    const pageHeight = useLandscape ? landscapePageHeight : portraitPageHeight;
    const scaleX = pageWidth / width;
    const scaleY = pageHeight / height;
    const scale = Math.min(scaleX, scaleY, 1.5); // Cap at 1.5x to avoid over-enlarging small maps
    
    // Create hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.left = '-9999px';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Mind Map</title>
        <style>
          @media print {
            @page { 
              size: ${useLandscape ? 'landscape' : 'portrait'};
              margin: 0.4in;
            }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            background: ${bgColor};
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            padding: 0;
          }
          .mind-map-wrapper {
            transform: scale(${scale});
            transform-origin: center center;
          }
          .mind-map-container {
            position: relative;
            width: ${width}px;
            height: ${height}px;
          }
          .edges-layer {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
          }
        </style>
      </head>
      <body>
        <div class="mind-map-wrapper">
          <div class="mind-map-container">
            <svg class="edges-layer" width="${width}" height="${height}" style="shape-rendering: geometricPrecision;">
              ${edgesSvg}
            </svg>
            ${nodesHtml}
          </div>
        </div>
      </body>
      </html>
    `);
    doc.close();
    
    // Wait for content to load, then print
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Remove iframe after printing
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 100);
    };
  }, [nodes, edges, darkMode]);

  // Helper function to get contrasting text color
  const getContrastColor = (bgColor) => {
    if (!bgColor) return '#000000';
    const hex = bgColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 125 ? '#000000' : '#ffffff';
  };
  
  // History for undo/redo (capped)
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const pushHistory = useCallback((nextState) => {
    setHistory(prev => {
      const trimmed = prev.slice(0, historyIndex + 1);
      const updated = [...trimmed, nextState];
      // Cap history size
      if (updated.length > HISTORY_MAX) {
        return updated.slice(updated.length - HISTORY_MAX);
      }
      return updated;
    });
    setHistoryIndex(prev => {
      const base = Math.min(prev, HISTORY_MAX - 2);
      return base + 1;
    });
  }, [historyIndex]);

  const apiSaveTimerRef = useRef(null);

  // Save to API with debouncing and localStorage backup
  const saveToAPI = useCallback(async (nodesData, edgesData) => {
    // Always save to localStorage immediately for offline access
    try {
      localStorage.setItem(`mindMapData-${projectId}`, JSON.stringify({ nodes: nodesData, edges: edgesData }));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }

    // Debounce API save
    if (apiSaveTimerRef.current) {
      clearTimeout(apiSaveTimerRef.current);
    }

    apiSaveTimerRef.current = setTimeout(async () => {
      try {
        await saveMindMap(projectId, {
          nodes: nodesData,
          edges: edgesData
        });
        console.log('✅ Mind map saved to API');
      } catch (error) {
        console.error('Error saving to API (localStorage backup active):', error);
      }
    }, 1500);
  }, [projectId]);

  // Load data from API with localStorage fallback - project specific
  useEffect(() => {
    if (!projectId) return;
    
    const loadData = async () => {
      try {
        const response = await getMindMap(projectId);
        if (response?.data?.mindMap) {
          const { nodes: savedNodes, edges: savedEdges } = response.data.mindMap;
          if (savedNodes && Array.isArray(savedNodes)) {
            const nodesWithDarkMode = savedNodes.map(node => ({
              ...node,
              data: { ...node.data, darkMode }
            }));
            setNodes(nodesWithDarkMode);
            console.log('✅ Loaded mind map from API');
          }
          if (savedEdges && Array.isArray(savedEdges)) {
            setEdges(savedEdges);
          }
          // Update localStorage with API data
          localStorage.setItem(`mindMapData-${projectId}`, JSON.stringify({ nodes: savedNodes, edges: savedEdges }));
        } else {
          throw new Error('No data from API');
        }
      } catch (error) {
        console.log('🔍 No API data, checking localStorage...');
        // Fallback to localStorage
        const savedData = localStorage.getItem(`mindMapData-${projectId}`);
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            if (parsedData.nodes && Array.isArray(parsedData.nodes)) {
              const nodesWithDarkMode = parsedData.nodes.map(node => ({
                ...node,
                data: { ...node.data, darkMode }
              }));
              setNodes(nodesWithDarkMode);
              console.log('✅ Loaded mind map from localStorage');
              
              // Migrate to API
              setTimeout(() => {
                saveMindMap(projectId, { nodes: parsedData.nodes, edges: parsedData.edges || [] })
                  .then(() => console.log('✅ Migrated localStorage data to API'))
                  .catch(err => console.log('Migration to API pending:', err.message));
              }, 2000);
            }
            if (parsedData.edges && Array.isArray(parsedData.edges)) {
              setEdges(parsedData.edges);
            }
          } catch (parseError) {
            console.error(`Error loading mind map data for project ${projectId}:`, parseError);
          }
        }
      }
    };
    
    loadData();
    
    return () => {
      if (apiSaveTimerRef.current) {
        clearTimeout(apiSaveTimerRef.current);
      }
    };
  }, [projectId, setNodes, setEdges, darkMode]);

  // Update nodes with dark mode when it changes
  useEffect(() => {
    setNodes(nodes => processNodes(nodes));
  }, [darkMode, processNodes, setNodes]);

  // Debounced save whenever nodes or edges change - project specific
  useEffect(() => {
    if (!projectId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveToAPI(nodes, edges);
    }, 400);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [nodes, edges, projectId, saveToAPI]);

  // Connection mode is already defined in state above
  
  const baseEdgeStroke = useCallback(() => (darkMode ? '#888' : '#555'), [darkMode]);

  // Memoized MiniMap callbacks and style for performance
  const miniMapNodeColor = useCallback((n) => n.data?.color || (darkMode ? '#333333' : '#ffffff'), [darkMode]);
  const miniMapStyle = useMemo(() => ({
    bottom: 10,
    left: 10,
    width: 120,
    height: 80,
    backgroundColor: darkMode ? '#1a1a1a' : '#f5f5f5',
    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
    borderRadius: 0
  }), [darkMode]);

  // Helper function to create a new edge between nodes
  const createEdge = useCallback((sourceId, targetId, label = '') => {
    return {
      id: `edge-${sourceId}-${targetId}-${Date.now()}`,
      source: sourceId,
      target: targetId,
      sourceHandle: 'center-source',
      targetHandle: 'center-target',
      type: 'smoothstep', // Curved edges for organic look
      label: label || '',
      labelStyle: { 
        fontSize: 11, 
        fontWeight: 500,
        fill: darkMode ? '#aaa' : '#666',
        pointerEvents: 'all'
      },
      labelBgStyle: { 
        fill: darkMode ? '#1a1a2e' : '#ffffff',
        fillOpacity: 0.85,
        rx: 4,
        ry: 4
      },
      labelBgPadding: [4, 6],
      style: { 
        stroke: baseEdgeStroke(), 
        strokeWidth: 2,
        opacity: darkMode ? 0.8 : 0.7,
      },
      animated: false,
    };
  }, [darkMode, baseEdgeStroke]);
  
  // Helper function to check if a connection already exists
  const connectionExists = useCallback((sourceId, targetId) => {
    return edges.some(edge => 
      (edge.source === sourceId && edge.target === targetId) ||
      (edge.source === targetId && edge.target === sourceId)
    );
  }, [edges]);
  
  // Helper function to reset connecting state - uses functional update to avoid stale closure
  const resetConnectingState = useCallback(() => {
    // Reset connecting visual state on all nodes using functional update
    setNodes(currentNodes => currentNodes.map(n => ({
      ...n,
      data: {
        ...n.data,
        isConnecting: false
      }
    })));
    
    // Exit connecting mode
    setIsConnectingNodes(false);
    setConnectingNodeId(null);
  }, [setNodes]);

  // Handle node selection - optimized for center-to-center connections
  const onNodeClick = useCallback((event, node) => {
    event.stopPropagation();
    if (isConnectingNodes && connectingNodeId && connectingNodeId !== node.id) {
      const newEdge = createEdge(connectingNodeId, node.id);
      
      // Check if this connection already exists
      if (!connectionExists(connectingNodeId, node.id)) {
        // Use functional updates to get current state and batch operations
        setNodes(currentNodes => {
          setEdges(currentEdges => {
            const updatedEdges = [...currentEdges, newEdge];
            // Defer history update to avoid blocking
            queueMicrotask(() => {
              pushHistory({ nodes: currentNodes, edges: updatedEdges });
            });
            return updatedEdges;
          });
          return currentNodes;
        });
      }
      
      resetConnectingState();
      return;
    }
    // Set selected node first
    setSelectedNode(node);
    
    // If double-click, open the edit dialog
    if (event.detail === 2) {
      editingNodeRef.current = node; // Store in ref for reliable access in handleAddNode
      setNewNode({
        label: node.data.label,
        group: node.data.category || node.data.group || 'note',
        color: node.data.color || '#f0f0f0',
        shape: node.data.shape || 'rectangle',
        fontSize: node.data.fontSize || '14px',
        bold: node.data.bold || false,
        italic: node.data.italic || false,
        underline: node.data.underline || false
      });
      setOpenDialog(true);
    }
  }, [isConnectingNodes, connectingNodeId, createEdge, connectionExists, resetConnectingState, setNodes, setEdges, pushHistory]);

  // Handle connection start - optimized with functional update
  const handleStartConnection = useCallback((nodeId) => {
    setIsConnectingNodes(true);
    setConnectingNodeId(nodeId);
    
    // Highlight the node being connected from using functional update
    setNodes(currentNodes => currentNodes.map(node => {
      if (node.id === nodeId) {
        return {
          ...node,
          data: {
            ...node.data,
            isConnecting: true
          }
        };
      }
      return node;
    }));
  }, [setNodes]);
  
  // Edge styling utilities
  const getEdgeStyle = useCallback((isSelected) => {
    return {
      stroke: isSelected ? (darkMode ? '#4dabf5' : '#1976d2') : (darkMode ? '#888' : '#555'),
      strokeWidth: isSelected ? 2.5 : 1.8,
      opacity: isSelected ? 1 : (darkMode ? 0.85 : 0.75),
      zIndex: isSelected ? 100 : 50 // Keep edges (and arrows) above nodes
    };
  }, [darkMode]);

  const lastSelectedEdgeIdRef = useRef(null);
  const clearEdgeSelection = useCallback(() => {
    if (!lastSelectedEdgeIdRef.current) return;
    const prevId = lastSelectedEdgeIdRef.current;
    lastSelectedEdgeIdRef.current = null;
    setSelectedEdge(null);
    setEdges(eds => eds.map(e => {
      if (e.id !== prevId) return e;
      return {
        ...e,
        selected: false,
        style: { ...e.style, ...getEdgeStyle(false) }
      };
    }));
  }, [getEdgeStyle]);

  // Handle edge selection
  const selectEdge = useCallback((edge) => {
    const newId = edge.id;
    const prevId = lastSelectedEdgeIdRef.current;
    lastSelectedEdgeIdRef.current = newId;
    setSelectedEdge(edge);
    setEdges(eds => eds.map(e => {
      if (e.id === newId) {
        return { ...e, selected: true, style: { ...e.style, ...getEdgeStyle(true) } };
      }
      if (prevId && e.id === prevId) {
        return { ...e, selected: false, style: { ...e.style, ...getEdgeStyle(false) } };
      }
      return e;
    }));
  }, [getEdgeStyle]);

  // Delete the currently selected edge
  const deleteSelectedEdge = useCallback(() => {
    if (selectedEdge) {
      setEdges(eds => {
        const updatedEdges = eds.filter(e => e.id !== selectedEdge.id);
        // Update history (capped)
        pushHistory({ nodes, edges: updatedEdges });
        
        return updatedEdges;
      });
      
      setSelectedEdge(null);
    }
  }, [selectedEdge, nodes, pushHistory]);


  // Create a new node at the specified position - optimized for smooth creation
  const createNodeAtPosition = useCallback((position) => {
    // Create a new node with default values
    const newNodeId = `node-${Date.now()}`;
    const newNodeData = {
      id: newNodeId,
      type: 'custom',
      position,
      data: { 
        label: 'New Note', 
        darkMode,
        color: darkMode ? '#2d2d2d' : '#f0f0f0',
        shape: 'rectangle',
        fontSize: '14px',
        bold: false,
        italic: false,
        underline: false
      }
    };
    
    // Use functional updates to avoid stale closures and batch updates
    setNodes(nds => {
      const updatedNodes = [...nds, newNodeData];
      // Defer history update to next tick to avoid blocking render
      queueMicrotask(() => {
        setEdges(currentEdges => {
          pushHistory({ nodes: updatedNodes, edges: currentEdges });
          return currentEdges;
        });
      });
      return updatedNodes;
    });
    
    // Exit node creation mode after placing a node
    setIsCreatingNode(false);
  }, [darkMode, pushHistory, setNodes, setEdges]);

  const onPaneClick = useCallback((event) => {
    // Clear any selected edge
    clearEdgeSelection();
    
    // Clear node selection
    setSelectedNode(null);
    
    const target = event?.target;
    const clickedNode = target?.closest?.('.react-flow__node');
    const clickedPane = target?.closest?.('.react-flow__pane');
    
    // Handle double-click on pane for quick node creation
    if (event.detail === 2 && clickedPane && !clickedNode && reactFlowInstance && reactFlowWrapper.current) {
      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top
      });
      createNodeAtPosition(position);
      return;
    }
    
    // Handle single-click in node creation mode
    if (
      isCreatingNode &&
      reactFlowInstance &&
      reactFlowWrapper.current &&
      clickedPane &&
      !clickedNode
    ) {
      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top
      });
      createNodeAtPosition(position);
    }
  }, [isCreatingNode, reactFlowInstance, clearEdgeSelection, createNodeAtPosition]);
  
  // Double-click on pane is handled via onPaneClick with event.detail === 2
  
  // Handle edge click for selection
  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    selectEdge(edge);
  }, [selectEdge]);
  
  // Handle connection creation with styling for professional mind map
  const onConnect = useCallback((params) => {
    // Find source and target nodes
    const sourceNode = nodes.find(node => node.id === params.source);
    const targetNode = nodes.find(node => node.id === params.target);
    
    // Don't create connection if nodes don't exist
    if (!sourceNode || !targetNode) return;
    
    // Check if this connection already exists (in either direction)
    const connectionExists = edges.some(edge => 
      (edge.source === params.source && edge.target === params.target) ||
      (edge.source === params.target && edge.target === params.source)
    );
    
    if (connectionExists) return; // Don't create duplicate connections
    
    // Create a new edge with styling for professional mind map (straight lines)
    const newEdge = {
      ...params,
      id: `edge-${params.source}-${params.target}-${Date.now()}`,
      type: 'straight', // Use straight lines for professional appearance
      animated: false,
      // Force connections to use the center handles
      sourceHandle: 'center-source',
      targetHandle: 'center-target',
      // Professional line styling with dark mode support
      style: { 
        stroke: darkMode ? '#888' : '#555', // Adaptive color based on mode
        strokeWidth: 1.8, // Slightly thicker for better visibility
        opacity: darkMode ? 0.85 : 0.75, // Subtle transparency for professional look
        zIndex: 50
      }
    };
    
    // Add the edge and update history in a single operation for better performance
    setEdges(eds => {
      const updatedEdges = addEdge(newEdge, eds);
      // Update history (capped)
      pushHistory({ nodes, edges: updatedEdges });
      
      return updatedEdges;
    });
  }, [nodes, edges, setEdges, pushHistory, darkMode]);

  // Handle adding a new node - Scapple style
  const handleAddNode = () => {
    if (!newNode.label) return;
    
    // Check if we're editing an existing node (use ref for reliable access)
    const nodeToEdit = editingNodeRef.current || selectedNode;
    
    if (nodeToEdit) {
      const updatedNodes = nodes.map(node => {
        if (node.id === nodeToEdit.id) {
          return {
            ...node,
            data: {
              ...node.data,
              label: newNode.label,
              color: newNode.color,
              category: newNode.group,
              shape: newNode.shape,
              fontSize: newNode.fontSize,
              bold: newNode.bold,
              italic: newNode.italic,
              underline: newNode.underline,
              image: newNode.image || node.data.image || null,
              stackOrder: newNode.stackOrder || 0
            }
          };
        }
        return node;
      });
      
      setNodes(updatedNodes);
      
      // Update history (capped)
      const currentState = { nodes: updatedNodes, edges };
      pushHistory(currentState);
    } else {
      // Create a new node - Scapple style
      const newId = `node-${Date.now()}`;
      let position;
      
      // If we have a mouse position from pane click, use that
      if (mousePosition.x !== 0 || mousePosition.y !== 0) {
        position = mousePosition;
      } else if (selectedNode) {
        // Position near selected node - slightly offset
        position = {
          x: selectedNode.position.x + 160, // More intentional positioning
          y: selectedNode.position.y + 30
        };
      } else {
        // Center position for first node, or visible area center
        position = nodes.length === 0 
          ? { x: 0, y: 0 } 
          : { x: 0, y: 0 }; // Will be centered in viewport
      }
      
      // Create new node with all styling properties including darkMode
      const newNodeObj = {
        id: newId,
        type: 'custom',
        position,
        data: {
          label: newNode.label,
          color: newNode.color,
          category: newNode.group,
          shape: newNode.shape,
          fontSize: newNode.fontSize,
          bold: newNode.bold,
          italic: newNode.italic,
          underline: newNode.underline,
          image: newNode.image || null,
          stackOrder: 0,
          darkMode
        }
      };
      
      // Use functional updates to batch state changes
      setNodes(currentNodes => {
        const updatedNodes = [...currentNodes, newNodeObj];
        
        // Create an edge if a node was selected
        if (selectedNode) {
          const newEdge = createEdge(selectedNode.id, newId);
          
          setEdges(currentEdges => {
            const updatedEdges = [...currentEdges, newEdge];
            // Defer history update
            queueMicrotask(() => pushHistory({ nodes: updatedNodes, edges: updatedEdges }));
            return updatedEdges;
          });
        } else {
          // Defer history update
          setEdges(currentEdges => {
            queueMicrotask(() => pushHistory({ nodes: updatedNodes, edges: currentEdges }));
            return currentEdges;
          });
        }
        
        return updatedNodes;
      });
      
      // Select the newly created node
      setSelectedNode(newNodeObj);
    }
    
    // Reset and close dialog
    editingNodeRef.current = null; // Clear editing ref
    setOpenDialog(false);
    
    // Keep some properties for next node creation
    setNewNode({ 
      label: '', 
      group: newNode.group, 
      color: newNode.color, 
      shape: newNode.shape,
      fontSize: newNode.fontSize,
      bold: newNode.bold,
      italic: newNode.italic,
      underline: newNode.underline,
      stackOrder: 0
    });
    
    setMousePosition({ x: 0, y: 0 });
  };
  
  // Handle node deletion - supports both single and multi-select
  const handleDeleteNode = () => {
    // Get all node IDs to delete (from multi-select or single select)
    const nodeIdsToDelete = selectedNodes.length > 0 
      ? selectedNodes.map(n => n.id) 
      : (selectedNode ? [selectedNode.id] : []);
    
    if (nodeIdsToDelete.length === 0) return;
    
    // Use functional updates to batch state changes
    setNodes(currentNodes => {
      const updatedNodes = currentNodes.filter(node => !nodeIdsToDelete.includes(node.id));
      
      setEdges(currentEdges => {
        const updatedEdges = currentEdges.filter(
          edge => !nodeIdsToDelete.includes(edge.source) && !nodeIdsToDelete.includes(edge.target)
        );
        // Defer history update
        queueMicrotask(() => pushHistory({ nodes: updatedNodes, edges: updatedEdges }));
        return updatedEdges;
      });
      
      return updatedNodes;
    });
    
    setSelectedNode(null);
    setSelectedNodes([]);
  };

  // Undo function
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      
      setNodes(previousState.nodes);
      setEdges(previousState.edges);
      setHistoryIndex(newIndex);
    }
  };

  // Redo function
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
      setHistoryIndex(newIndex);
    }
  };

  // Open dialog for adding/editing nodes
  const handleOpenDialog = () => {
    if (selectedNode) {
      editingNodeRef.current = selectedNode; // Store in ref for reliable access
      setNewNode({
        label: selectedNode.data.label,
        group: selectedNode.data.category || selectedNode.data.group || 'note',
        color: selectedNode.data.color || '#f0f0f0',
        shape: selectedNode.data.shape || 'rectangle',
        fontSize: selectedNode.data.fontSize || '14px',
        bold: selectedNode.data.bold || false,
        italic: selectedNode.data.italic || false,
        underline: selectedNode.data.underline || false
      });
    } else {
      editingNodeRef.current = null; // Clear ref for new node creation
      setNewNode({ 
        label: '', 
        group: 'note', 
        color: '#f0f0f0', 
        shape: 'rectangle',
        fontSize: '14px',
        bold: false,
        italic: false,
        underline: false,
        stackOrder: 0
      });
    }
    setOpenDialog(true);
  };
  
  // Start connection from selected node
  const startConnectionFromSelected = useCallback(() => {
    if (selectedNode) {
      handleStartConnection(selectedNode.id);
    }
  }, [selectedNode, handleStartConnection]);
  
  // Handle keyboard shortcuts - enhanced for Scapple-like experience
  const handleKeyDown = useCallback((e) => {
    // Skip if we're in a text field
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    
    switch (e.key) {
      case 'n': // Create new node
        setIsCreatingNode(true);
        setSelectedNode(null); // Clear selected node when creating a new one
        console.log('N key pressed, creating mode activated');
        break;
      case 'c': // Connect nodes
        if (selectedNode) handleStartConnection();
        break;
      case 'Delete': // Delete node(s)
      case 'Backspace': // Also allow backspace for delete
        if (selectedNodes.length > 0 || selectedNode) handleDeleteNode();
        else if (selectedEdge) deleteSelectedEdge();
        break;
      case 'Enter': // Edit node
        if (selectedNode) handleOpenDialog();
        break;
      case 'Escape': // Cancel current operation
        if (isCreatingNode) {
          setIsCreatingNode(false);
        } else if (isConnectingNodes) {
          setIsConnectingNodes(false);
          setConnectingNodeId(null);
        }
        break;
      case 'z': // Undo
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleUndo();
        }
        break;
      case 'y': // Redo
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleRedo();
        }
        break;
      case '+': // Zoom in
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleZoomIn();
        }
        break;
      case '-': // Zoom out
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleZoomOut();
        }
        break;
      case '0': // Fit view
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleFitView();
        }
        break;
      case 'a': // Select all
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          // Select all nodes
          const allNodeIds = nodes.map(node => node.id);
          // ReactFlow has built-in selection state that we can use
          const selectionEvent = {
            nodes: nodes.map(node => ({ id: node.id, selected: true })),
            edges: []
          };
          onNodesChange(selectionEvent);
        }
        break;
      default:
        break;
    }
  }, [selectedNode, startConnectionFromSelected, isCreatingNode, isConnectingNodes, nodes, onNodesChange]);
  
  // Add keyboard event listener
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <Box sx={{ 
      width: '100%', 
      height: '80vh', 
      display: 'flex', 
      flexDirection: 'column',
    }}>
      {/* Premium toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          p: 1,
          pl: 1.25,
          pr: 1.25,
          borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          position: 'sticky',
          top: 0,
          zIndex: 5,
          minHeight: 52
        }}
      >
        <Tooltip title="Delete selected (Del)">
          <span>
            <IconButton
              size="small"
              onClick={handleDeleteNode}
              disabled={!selectedNode && selectedNodes.length === 0}
              sx={{
                ...toolbarButtonStyle,
                color: (selectedNode || selectedNodes.length > 0) ? '#e57373' : toolbarButtonStyle.color
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            color: darkMode ? '#b5b5c0' : '#5a6170',
            ml: 1,
            minWidth: { xs: '100%', sm: 'auto' },
            letterSpacing: 0.2
          }}
        >
          Double-click canvas to add • double-click a note to edit • drag from a note handle to connect
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Undo (Ctrl+Z)">
          <span>
            <IconButton
              size="small"
              onClick={handleUndo}
              disabled={historyIndex <= 0}
              sx={toolbarButtonStyle}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Redo (Ctrl+Y)">
          <span>
            <IconButton
              size="small"
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1}
              sx={toolbarButtonStyle}
            >
              <RedoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ borderLeft: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)', height: 24, mx: 1 }} />

        <Tooltip title="Zoom out (Ctrl+-)">
          <span>
            <IconButton size="small" onClick={handleZoomOut} sx={toolbarButtonStyle}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            color: darkMode ? '#e0e0e0' : '#555',
            minWidth: '44px',
            textAlign: 'center'
          }}
        >
          {Math.round(viewportTransform.zoom * 100)}%
        </Typography>
        <Tooltip title="Zoom in (Ctrl++)">
          <span>
            <IconButton size="small" onClick={handleZoomIn} sx={toolbarButtonStyle}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Fit view (Ctrl+0)">
          <span>
            <IconButton size="small" onClick={handleFitView} sx={toolbarButtonStyle}>
              <FitScreenIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Box sx={{ borderLeft: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)', height: 24, mx: 1 }} />

        <Tooltip title="Print Mind Map">
          <span>
            <IconButton size="small" onClick={handlePrint} sx={toolbarButtonStyle}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* React Flow Canvas */}
      <Box sx={{ 
        flexGrow: 1, 
        backgroundColor: darkMode ? '#0a0a0a' : '#fff',
        position: 'relative',
        overflow: 'hidden'
      }} ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onEdgeClick={onEdgeClick}
          onInit={onInit}
          nodeTypes={nodeTypes}
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          fitViewOptions={{ padding: 0.2, includeHiddenNodes: true, minZoom: 1, maxZoom: 1 }}
          attributionPosition="bottom-right"
          connectionLineStyle={{ 
            stroke: darkMode ? '#888' : '#555', 
            strokeWidth: 1.8, // Slightly thicker for better visibility
            strokeDasharray: '4,3', // More refined dashed line while connecting
            opacity: darkMode ? 0.9 : 0.8,
            zIndex: 1000 // Need high z-index for connection line during drag
          }}
          connectionLineType="straight" // Straight lines for professional appearance
          connectionRadius={24} // Ensure connections go to the center
          snapToGrid={false} // Turn off grid snapping for completely free placement
          deleteKeyCode={null} // Disable default delete to use our custom handler
          defaultEdgeOptions={{
            type: 'straight', // Straight lines for professional appearance
            animated: false,
            style: { 
              stroke: baseEdgeStroke(), 
              strokeWidth: 1.8, // Slightly thicker for better visibility
              opacity: darkMode ? 0.85 : 0.75, // Subtle transparency for a professional look
              zIndex: 50 // Keep edges above nodes so arrowheads stay visible
            },
            sourceHandle: 'center-source', // Always use center source handle
            targetHandle: 'center-target', // Always use center target handle
          }}
          connectionMode="loose" // Allow connections from anywhere to anywhere
          elevateEdgesOnSelect={false} // Keep connections behind nodes even when selected
          minZoom={0.1} // Allow more zoom out for larger mind maps
          maxZoom={2.5} // Allow closer zoom for detail work
          zoomOnScroll={true}
          panOnScroll={false} // Disable pan on scroll so mouse wheel zooms directly
          zoomActivationKeyCode={null} // No key required - mouse wheel zooms directly
          panOnDrag={[1, 2]} // Pan with middle mouse (1) or right mouse (2) button
          selectionOnDrag={true} // Left-click drag creates selection box
          multiSelectionKeyCode="Shift" // Use shift for multi-select like Scapple
          zoomOnDoubleClick={false} // Disable zoom on double-click to allow double-click for editing
          onMove={onZoomChange} // Track zoom changes
          onSelectionChange={(params) => {
            setSelectedNodes(params.nodes);
            // If a single node is selected, also update selectedNode for compatibility
            if (params.nodes.length === 1) {
              setSelectedNode(params.nodes[0]);
            } else if (params.nodes.length === 0) {
              setSelectedNode(null);
            }
          }}
          selectionKeyCode={null} // Allow selection without key modifier
        >
          {/* Collapsible MiniMap with toggle button */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              zIndex: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.5
            }}
          >
            <IconButton
              size="small"
              onClick={() => setShowMiniMap(prev => !prev)}
              sx={{
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                borderRadius: 0,
                width: 28,
                height: 28,
                color: darkMode ? '#aaa' : '#666',
                '&:hover': {
                  backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'
                }
              }}
            >
              <FitScreenIcon sx={{ fontSize: 16 }} />
            </IconButton>
            {showMiniMap && (
              <MiniMap 
                nodeStrokeColor={miniMapNodeColor}
                nodeColor={miniMapNodeColor}
                nodeBorderRadius={0}
                maskColor={darkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.1)'}
                style={{ 
                  position: 'relative',
                  bottom: 'auto',
                  left: 'auto',
                  width: 120,
                  height: 80,
                  backgroundColor: darkMode ? '#1a1a1a' : '#f5f5f5',
                  border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                  borderRadius: 0
                }}
                pannable
                zoomable={false}
              />
            )}
          </Box>
          <Background 
            color={darkMode ? '#4a4f5a' : '#c0c6d4'} 
            gap={28} 
            size={0.6}
            style={{
              backgroundColor: 'transparent',
              opacity: darkMode ? 0.35 : 0.28
            }}
          />
        </ReactFlow>
      </Box>
      
      <ProModal
        open={openDialog}
        onClose={() => { editingNodeRef.current = null; setOpenDialog(false); }}
        title={selectedNode ? 'Edit Mind Map Node' : 'Design Mind Map Node'}
        eyebrow={selectedNode ? 'REFINE NOTE' : 'NEW NOTE'}
        icon={<ColorLensIcon />}
        actions={(
          <>
            <Button onClick={() => { editingNodeRef.current = null; setOpenDialog(false); }} sx={{ borderRadius: 1, textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleAddNode}
              variant="contained"
              disabled={!newNode.label.trim()}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                px: 3,
                fontWeight: 600
              }}
            >
              {selectedNode ? 'Save Node' : 'Create Node'}
            </Button>
          </>
        )}
      >
        <>
          <Box>
            <Typography variant="overline" sx={{ letterSpacing: 2, color: muiTheme.palette.text.secondary }}>
              Content
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              label="Note Text"
              fullWidth
              multiline
              rows={3}
              value={newNode.label}
              onChange={(e) => setNewNode({ ...newNode, label: e.target.value })}
              InputProps={{
                sx: {
                  borderRadius: 1,
                  backgroundColor: alpha(muiTheme.palette.background.paper, darkMode ? 0.4 : 0.7)
                }
              }}
            />
          </Box>

          <Divider flexItem />

          <Box>
            <Typography variant="overline" sx={{ letterSpacing: 2, color: muiTheme.palette.text.secondary }}>
              Palette
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(42px, 1fr))',
                gap: 1,
                mt: 1
              }}
            >
              {NODE_COLOR_OPTIONS.map((option) => {
                const isActive = newNode.color === option.value;
                return (
                  <Box
                    key={option.value}
                    component="button"
                    type="button"
                    aria-label={option.label}
                    onClick={() => setNewNode({ ...newNode, color: option.value })}
                    sx={{
                      height: 42,
                      borderRadius: 1,
                      border: isActive
                        ? `2px solid ${muiTheme.palette.primary.main}`
                        : '1px solid transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: option.value,
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      }
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          <Divider flexItem />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <FormControl>
              <InputLabel>Note Style</InputLabel>
              <Select
                size="small"
                value={newNode.shape}
                label="Note Style"
                onChange={(e) => setNewNode({ ...newNode, shape: e.target.value })}
              >
                {SHAPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel>Font Size</InputLabel>
              <Select
                size="small"
                value={newNode.fontSize}
                label="Font Size"
                onChange={(e) => setNewNode({ ...newNode, fontSize: e.target.value })}
              >
                {FONT_SIZE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="overline" sx={{ letterSpacing: 2, color: muiTheme.palette.text.secondary }}>
              Text Treatment
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
              {[/* eslint-disable react/prop-types */
                { key: 'bold', label: 'B', style: { fontWeight: 700 } },
                { key: 'italic', label: 'I', style: { fontStyle: 'italic' } },
                { key: 'underline', label: 'U', style: { textDecoration: 'underline' } }
              ].map((control) => {
                const active = !!newNode[control.key];
                return (
                  <Button
                    key={control.key}
                    size="small"
                    variant={active ? 'contained' : 'outlined'}
                    onClick={() => setNewNode({ ...newNode, [control.key]: !newNode[control.key] })}
                    sx={{
                      minWidth: 56,
                      borderRadius: 1,
                      textTransform: 'none',
                      ...control.style
                    }}
                  >
                    {control.label}
                  </Button>
                );
              })}
            </Box>
          </Box>

          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={newNode.group}
              label="Category"
              onChange={(e) => setNewNode({ ...newNode, group: e.target.value })}
              renderValue={(selected) => {
                const option = NODE_CATEGORY_OPTIONS.find(o => o.value === selected);
                return option ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 16, 
                      height: 16, 
                      borderRadius: '50%', 
                      backgroundColor: option.color,
                      border: '1px solid rgba(0,0,0,0.1)',
                      flexShrink: 0
                    }} />
                    {option.label}
                  </Box>
                ) : selected;
              }}
            >
              {NODE_CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ 
                    width: 16, 
                    height: 16, 
                    borderRadius: '50%', 
                    backgroundColor: option.color,
                    border: '1px solid rgba(0,0,0,0.1)',
                    flexShrink: 0
                  }} />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        </>
      </ProModal>
    </Box>
  );
};

MindMapFlow.propTypes = {
  projectId: PropTypes.string
};


export default MindMapFlow;
